import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserAppointments } from '../appointment/appointmentFunctions.js';
import ChatButton from '../buttons/Chat_button.svg';
import { authenticateMatrixClient } from '../../services/matrixAuthService.js';

// Custom hooks
import { usePatientAppointmentStorage } from './hooks/usePatientAppointmentStorage';
import { usePatientAppointmentActions } from './hooks/usePatientAppointmentActions';

// Components
import PatientAppointmentTableHeader from './components/PatientAppointmentTableHeader';
import PatientAppointmentRow from './components/PatientAppointmentRow';
import PatientDeleteAppointmentModal from './components/PatientDeleteAppointmentModal';
import Pagination from './components/Pagination';
import PatientAppointmentDetails from './PatientAppointmentDetails';
import ViewChatModal from './ViewChatModalPage';

const ViewPatientAppointments = () => {
    // Redux state
    const dispatch = useDispatch();
    const appointments = useSelector(state => state.appointmentDetails?.appointmentsForUser);
    const loggedInUser = useSelector(state => state.loginDetails?.loggedInUser);
    const serviceTypes = useSelector(state => state.loginDetails?.serviceTypes);
    const roomsNotificationCount = useSelector(state => state.appointmentDetails?.roomsNotificationCount);
    const token = useSelector(state => state.loginDetails?.jwtToken);
    const currentPageNum = useSelector(state => state.appointmentDetails?.currentPageNumber);

    // Local state
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(null);
    
    // Track Matrix authentication attempts to prevent rate limiting
    const matrixAuthAttempted = useRef(false);

    // Custom hooks
    usePatientAppointmentStorage(loggedInUser, appointments, isInitialLoad, dispatch, token);

    // Get matrix client from Redux state
    const matrixClient = useSelector(state => state.chatDetails?.matrixClient);

    // Trigger Matrix authentication on component mount for patient users (with rate limiting)
    useEffect(() => {
        console.log('=== PATIENT COMPONENT - MATRIX AUTH CHECK ===');
        console.log('loggedInUser:', !!loggedInUser);
        console.log('matrixClient:', !!matrixClient);
        console.log('matrixAuthAttempted.current:', matrixAuthAttempted.current);
        
        if (loggedInUser && !matrixClient && !matrixAuthAttempted.current) {
            console.log('=== PATIENT COMPONENT - TRIGGERING MATRIX AUTH ===');
            console.log('Patient user available:', !!loggedInUser);
            console.log('Matrix client not available:', !matrixClient);
            console.log('User Type:', loggedInUser.user_type);
            
            // Mark that we've attempted authentication IMMEDIATELY to prevent race conditions
            matrixAuthAttempted.current = true;
            console.log('Set patient matrixAuthAttempted to true');
            
            authenticateMatrixClient(loggedInUser, dispatch)
                .then(() => {
                    console.log('✅ PATIENT Matrix authentication completed successfully');
                })
                .catch((error) => {
                    console.error('❌ PATIENT Matrix authentication failed:', error);
                    // Only reset for non-rate-limit errors
                    if (!error.message?.includes('Too Many Requests') && !error.message?.includes('M_LIMIT_EXCEEDED')) {
                        console.log('Resetting patient auth flag for retry in 30 seconds');
                        setTimeout(() => {
                            matrixAuthAttempted.current = false;
                        }, 30000);
                    } else {
                        console.log('Rate limiting error - NOT resetting patient auth flag');
                    }
                });
        } else {
            console.log('=== PATIENT COMPONENT - MATRIX AUTH STATUS ===');
            console.log('Patient user available:', !!loggedInUser);
            console.log('Matrix client available:', !!matrixClient);
            console.log('Auth attempt already made:', matrixAuthAttempted.current);
            console.log('Already authenticated, no user, or auth attempted');
        }
        console.log('=== END PATIENT COMPONENT - MATRIX AUTH DEBUG ===');
    }, [loggedInUser, matrixClient, dispatch]);

    // Auto-refresh appointments every 30 seconds
    useEffect(() => {
        if (loggedInUser && token) {
            console.log('=== SETTING UP AUTO-REFRESH (PATIENT) ===');
            console.log('Logged in User ID:', loggedInUser._id);
            console.log('Token available:', !!token);
            console.log('Current appointments count:', appointments?.length || 0);
            console.log('=== END AUTO-REFRESH SETUP ===');
            
            // Set up interval for auto-refresh
            const interval = setInterval(() => {
                console.log('=== AUTO-REFRESH TRIGGERED (PATIENT) ===');
                console.log('Timestamp:', new Date().toISOString());
                console.log('Fetching appointments for user:', loggedInUser._id);
                console.log('Current appointments count before refresh:', appointments?.length || 0);
                fetchUserAppointments(loggedInUser._id, 0, dispatch, token);
                console.log('=== END AUTO-REFRESH TRIGGERED ===');
            }, 300000); // 30 seconds

            setRefreshInterval(interval);
            console.log('Auto-refresh interval set up (30 seconds)');

            // Cleanup interval on component unmount
            return () => {
                if (interval) {
                    console.log('=== CLEANING UP AUTO-REFRESH INTERVAL (PATIENT) ===');
                    clearInterval(interval);
                    console.log('Auto-refresh interval cleared');
                }
            };
        } else {
            console.log('=== AUTO-REFRESH NOT SET UP (PATIENT) ===');
            console.log('loggedInUser available:', !!loggedInUser);
            console.log('Token available:', !!token);
        }
    }, [loggedInUser, token, dispatch, appointments?.length]);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [refreshInterval]);
    
    const {
        showDeleteAppointment,
        isDeleting,
        showApptDetails,
        selectedAppointment,
        isSortAsc,
        handleApptDetailsShow,
        handleApptDetailsClose,
        handleDeleteApptShow,
        handleDeleteApptClose,
        handleDeleteApptSubmit,
        showNearestPracticesDiv,
        handleViewChat,
        sortByDate
    } = usePatientAppointmentActions(loggedInUser, token);

    // Set initial load to false after first data fetch
    useEffect(() => {
        if (appointments && appointments.length > 0) {
            setIsInitialLoad(false);
        }
    }, [appointments]);

    // Pagination handlers
    const handleLoadPrevious = () => {
        fetchUserAppointments(loggedInUser._id, currentPageNum - 2, dispatch, token);
    };

    const handleLoadNext = () => {
        fetchUserAppointments(loggedInUser._id, currentPageNum, dispatch, token);
    };

    // Check if there's a next page
    const hasNextPage = appointments && appointments.length >= 10;

    return (
        <div className="px-4 sm:px-6 py-6 space-y-6">
            <div className="w-full">
                {/* Header row */}
                <PatientAppointmentTableHeader 
                    onSortByDate={sortByDate}
                    isSortAsc={isSortAsc}
                />
                
                {/* Appointments list */}
                <div className="bg-white rounded-xl">
                    {(!appointments || appointments.length === 0) && (
                        <div className="p-8 text-center">
                            <div className="text-gray-500 text-lg">No Appointments Found.</div>
                        </div>
                    )}
                    
                    {appointments && appointments.map((appointment) => (
                        <React.Fragment key={appointment._id}>
                            <PatientAppointmentRow
                                appointment={appointment}
                                serviceTypes={serviceTypes}
                                onDetailsClick={handleApptDetailsShow}
                                onDeleteClick={handleDeleteApptShow}
                                onViewPracticesClick={showNearestPracticesDiv}
                                ChatButton={ChatButton}
                                roomsNotificationCount={roomsNotificationCount}
                                onViewChat={handleViewChat}
                                loggedInUser={loggedInUser}
                                dispatch={dispatch}
                            />
                        </React.Fragment>
                    ))}
                </div>
            </div>
            
            {/* Pagination */}
            <Pagination
                currentPage={currentPageNum}
                hasNextPage={hasNextPage}
                onLoadPrevious={handleLoadPrevious}
                onLoadNext={handleLoadNext}
            />
            
            {/* Modals */}
            <PatientAppointmentDetails
                showApptDetails={showApptDetails}
                selectedAppointment={selectedAppointment}
                serviceTypes={serviceTypes}
                handleApptDetailsClose={handleApptDetailsClose}
                dispatch={dispatch}
                loggedInUser={loggedInUser}
            />
            
            <ViewChatModal
                selectedAppointment={selectedAppointment}
                serviceTypes={serviceTypes}
            />
            
            <PatientDeleteAppointmentModal
                show={showDeleteAppointment}
                isDeleting={isDeleting}
                onConfirm={handleDeleteApptSubmit}
                onCancel={handleDeleteApptClose}
            />
        </div>
    );
};

export default ViewPatientAppointments;
